import { PassesGrid } from "@/pages/catalogs/pages/edger-rolls/components/passes/PassesGrid";
import { RollOperation } from "@/pages/rollshop/models/Operation";
import { ConfirmActionModal } from "@/pages/rollshop/pages/operations/components/ConfirmActionModal";
import {
	getRollOperations,
	upsertRoll,
} from "@/pages/rollshop/repositories/OperationRepository";
import { LocationTreeSelector } from "@/shared/selectors";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ActionCard } from "./ActionCard";

type Props = {
	show: boolean;
	onHide: (newDiameter?: number) => void;
	rollId: number;
};

export const useRollOperationsById = ({
	partId,
	enabled = true,
}: {
	partId: number | null;
	enabled?: boolean;
}) => {
	return useQuery<RollOperation[], Error>({
		queryKey: ["rollOperations", partId],
		queryFn: ({ signal }) => getRollOperations({ partId }, signal),
		enabled: enabled,
	});
};

export const MaintenanceRollModal = ({ show, onHide, rollId }: Props) => {
	const [error, setError] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [selectedAction, setSelectedAction] = useState<
		"Cutting" | "Dressing" | "Spray Welding" | "Cleaning" | ""
	>("");
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [rollOperation, setRollOperation] = useState<RollOperation | null>(
		null,
	);

	const { control, reset, watch } = useForm<RollOperation>({
		mode: "onChange",
	});

	const diameter = watch("Diameter");
	const locationId = watch("LocationId");

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		isSuccess,
	} = useRollOperationsById({
		partId: rollId,
		enabled: show,
	});

	const getOperationData = (typeCode: string) => {
		const op = rows?.find(
			(o) => o.PartId === rollId && o.RollOperationTypeCode === typeCode,
		);
		return {
			time: op?.OperationTimestamp ?? null,
			comments: op?.OperationComments,
		};
	};

	const operations = {
		cutting: getOperationData("C"),
		dressing: getOperationData("S"),
		sprayWelding: getOperationData("W"),
		cleaning: getOperationData("L"),
	};

	const queryClient = useQueryClient();
	const updateRoll = useMutation(
		() => {
			setIsSubmitLoading(true);
			return upsertRoll({
				partId: rollId,
				diameter: diameter,
				locationId: locationId,
			});
		},
		{
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: ["rolls"] });
				onHide(Number(diameter));
			},
			onError: (error: Error) => {
				setError(error.message);
			},
			onSettled: () => setIsSubmitLoading(false),
		},
	);

	const onPressedAction = (
		action: "Cutting" | "Dressing" | "Spray Welding" | "Cleaning",
	) => {
		setSelectedAction(action);
		setShowConfirmModal(true);
	};

	const onChangeRealType = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		onChange: (value: string) => void,
	) => {
		const value = e.target.value.replace(",", ".");
		if (value === "" || /^(\d*\.?\d{0,3}|\d+\.)$/.test(value)) {
			onChange(value);
		}
	};
	const onBlurRealType = (onChange: (value: string) => void, value: string) => {
		const numValue = parseFloat(value);
		if (!isNaN(numValue)) {
			onChange(numValue.toFixed(3));
		} else if (value === "") {
			onChange("");
		}
	};

	useEffect(() => {
		if (isError) {
			setError(e?.message);
		}
	}, [isError, e]);

	useEffect(() => {
		if (isSuccess) {
			setRollOperation(rows.length > 0 ? rows[0] : null);
			reset(rows[0]);
		}
	}, [isSuccess, rollId]);

	return (
		<>
			<MesfModal
				title={`MAINTENANCE: ${rollOperation?.PartName ?? ""}`}
				open={show}
				handleClose={() => onHide()}
				id="maintenance-roll-modal"
				maxWidth="sm"
			>
				<MesfModal.Content dividers>
					{isLoading ? (
						<Box display="flex" justifyContent="center" py={4}>
							<CircularProgress />
						</Box>
					) : (
						<>
							<Typography
								variant="subtitle2"
								color="textSecondary"
								fontWeight={600}
								gutterBottom
							>
								Maintenance Actions
							</Typography>
							<Grid2 container spacing={1.5}>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ActionCard
										action="Cutting"
										comments={operations.cutting.comments}
										lastUpdate={operations.cutting.time}
										onPressed={() => onPressedAction("Cutting")}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ActionCard
										action="Dressing"
										comments={operations.dressing.comments}
										lastUpdate={operations.dressing.time}
										onPressed={() => onPressedAction("Dressing")}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ActionCard
										action="Spray Welding"
										comments={operations.sprayWelding.comments}
										lastUpdate={operations.sprayWelding.time}
										onPressed={() => onPressedAction("Spray Welding")}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ActionCard
										action="Cleaning"
										comments={operations.cleaning.comments}
										lastUpdate={operations.cleaning.time}
										onPressed={() => onPressedAction("Cleaning")}
									/>
								</Grid2>
							</Grid2>

							<Divider sx={{ my: 2.5 }} />

							<Typography
								variant="subtitle2"
								color="textSecondary"
								fontWeight={600}
								gutterBottom
							>
								Roll Properties
							</Typography>
							<Grid2 container spacing={2} alignItems="center">
								<Grid2 size={{ xs: 12, md: 6 }}>
									<Controller
										name="LocationId"
										control={control}
										render={({ field }) => (
											<LocationTreeSelector
												label="Location"
												value={field.value}
												onChange={field.onChange}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<Controller
										name="Diameter"
										control={control}
										render={({
											field: { onChange, value },
											fieldState: { error },
										}) => (
											<TextField
												label="Diameter"
												variant="outlined"
												fullWidth
												size="small"
												autoComplete="off"
												value={value ?? ""}
												onChange={(e) => onChangeRealType(e, onChange)}
												onBlur={() => onBlurRealType(onChange, String(value))}
												error={!!error}
												helperText={error?.message}
												slotProps={{
													htmlInput: {
														"data-test-id": "diameter-input",
													},
												}}
											/>
										)}
									/>
								</Grid2>
							</Grid2>

							{rollOperation?.RollTypeCode === "E" && (
								<>
									<Divider sx={{ my: 2.5 }} />
									<Typography
										variant="subtitle2"
										color="textSecondary"
										fontWeight={600}
										gutterBottom
									>
										Edit Passes
									</Typography>
									<PassesGrid partId={rollId} mode="edit" />
								</>
							)}
						</>
					)}
				</MesfModal.Content>
				<MesfModal.Actions sx={{ padding: "16px 20px 20px" }}>
					<Grid2 container spacing={1.5} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => onHide()}
								size="medium"
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
								disabled={isSubmitLoading}
								variant="contained"
								color="primary"
								size="medium"
								onClick={() => updateRoll.mutate()}
								data-test-id="confirm-maintenance-button"
							>
								Save
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error upserting roll"
			/>
			<ConfirmActionModal
				action={selectedAction}
				roll={rollOperation}
				show={showConfirmModal}
				onHide={async (updated) => {
					setShowConfirmModal(false);
					if (updated) {
						await queryClient.invalidateQueries(["rollOperations"]);
					}
				}}
			/>
		</>
	);
};
